/**
 * Katalog Radia Adamowo: zamienia listę plików na pozycje `RadioItem`.
 *
 * Jedna konfiguracja playlisty: pliki wykrywa plugin builda, a tutaj nadajemy
 * im identyfikator, tytuł i wykonawcę. Niczego nie zmyślamy - jeśli plik nie
 * ma tagów ani czytelnej nazwy, dostaje neutralne „Radio Adamowo – Utwór 17".
 */

import { RADIO_CATALOG_FILES, type RadioCatalogFile } from './catalogFiles';
import { RADIO_STATION } from './radio.config';
import { slugify } from './radioLayout';
import type { BroadcastItem, RadioItem, RadioItemType, UpcomingItem } from './radio.types';
import { hourToWords } from './timeAnnouncements';

/**
 * Ręczne poprawki pozycji katalogu, kluczem jest `id`
 * (np. `music:utwor-17`, a dla pliku w podfolderze `music:night-utwor-17`).
 * Pozwala nadać tytuł, wykonawcę, okładkę, tagi lub wagę bez ruszania plików.
 */
export const RADIO_CATALOG_OVERRIDES: Readonly<Record<string, Partial<Omit<RadioItem, 'id'>>>> = {};

const BREAK_TITLES: Record<Exclude<RadioItemType, 'music' | 'vocal' | 'time'>, string> = {
  jingle: 'Jingiel',
  station_id: RADIO_STATION.name,
  interlude: 'Przerywnik',
};

function fileBaseName(src: string): string {
  const file = src.slice(src.lastIndexOf('/') + 1).replace(/\.[^.]+$/, '');
  try {
    return decodeURIComponent(file);
  } catch {
    return file;
  }
}

/** Identyfikator pozycji komunikatu godzinowego, np. `time:14`. */
export const getTimeItemId = (hour: number): string => `time:${String(hour).padStart(2, '0')}`;

function describeTrack(file: RadioCatalogFile, name: string): Pick<RadioItem, 'artist' | 'title'> {
  if (file.title) {
    return { artist: file.artist ?? RADIO_STATION.name, title: file.title };
  }

  // „Utwor (17)" → „Utwór 17"
  const numbered = /^utw[oó]r\s*\(?(\d+)\)?$/i.exec(name);
  if (numbered) {
    return { artist: RADIO_STATION.name, title: `Utwór ${Number(numbered[1])}` };
  }

  // „Wykonawca - Tytuł"
  const [artist, ...rest] = name.split(' - ');
  if (rest.length > 0 && artist.trim()) {
    return { artist: artist.trim(), title: rest.join(' - ').trim() };
  }

  return { artist: file.artist ?? RADIO_STATION.name, title: name };
}

function toRadioItem(file: RadioCatalogFile): RadioItem | null {
  const name = fileBaseName(file.src);
  const tags = file.tags ?? [];
  const base = { type: file.type, src: file.src, ...(tags.length > 0 ? { tags } : {}) };
  // Podfolder wchodzi do identyfikatora, więc ta sama nazwa pliku w dwóch folderach nie koliduje.
  const id = `${file.type}:${slugify([...tags, name].join(' '))}`;

  switch (file.type) {
    case 'music':
    case 'vocal':
      return { id, ...base, ...describeTrack(file, name) };
    case 'time': {
      // Plik `14.mp3` to komunikat godziny czternastej.
      const hour = Number(name);
      if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null;
      return { id: getTimeItemId(hour), ...base, title: `Jest godzina ${hourToWords(hour)}` };
    }
    default:
      return {
        id,
        ...base,
        title: file.title ?? BREAK_TITLES[file.type],
      };
  }
}

export function buildCatalog(
  files: readonly RadioCatalogFile[],
  overrides: Readonly<Record<string, Partial<Omit<RadioItem, 'id'>>>> = RADIO_CATALOG_OVERRIDES
): RadioItem[] {
  const seen = new Set<string>();

  return files.flatMap((file) => {
    const item = toRadioItem(file);
    if (!item || seen.has(item.id)) return [];
    seen.add(item.id);
    return [{ ...item, ...overrides[item.id] }];
  });
}

/** Katalog bieżącego wdrożenia. */
export const RADIO_CATALOG: readonly RadioItem[] = buildCatalog(RADIO_CATALOG_FILES);

/** Pozycja katalogu → pozycja na antenie (jedno źródło prawdy dla UI). */
export function toBroadcastItem(item: RadioItem, emission: number): BroadcastItem {
  return {
    id: `${item.id}#${emission}`,
    catalogId: item.id,
    type: item.type,
    artist: item.artist ?? null,
    title: item.title,
    artwork: item.artwork ?? null,
    duration: item.duration ?? null,
    startedAt: null,
  };
}

export function toUpcomingItem(item: RadioItem): UpcomingItem {
  return { type: item.type, artist: item.artist ?? null, title: item.title };
}
